import React, { useState } from 'react';
import api from '../../services/api';
import { localDateStr } from '../../utils/dateUtils';

/**
 * ExportButton
 * Props:
 *   endpoint: string  — e.g., '/export/clients'
 *   params: object    — current filter params to include in export
 *   label: string     — button label
 *   filename: string  — base filename (without extension)
 */
export default function ExportButton({ endpoint, params = {}, label = 'Export', filename }) {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const doExport = async (format) => {
    setLoading(true);
    setOpen(false);
    try {
      const resp = await api.get(endpoint, {
        params: { ...params, format },
        responseType: 'blob',
      });
      const ext = format === 'csv' ? 'csv' : 'xlsx';
      const name = `${filename || 'export'}_${localDateStr()}.${ext}`;
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed:', e);
      alert('Export failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen(o => !o)}
        disabled={loading}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '7px 12px', borderRadius: 7,
          border: '1px solid var(--border)', background: '#fff',
          color: 'var(--text-secondary)', cursor: loading ? 'not-allowed' : 'pointer',
          fontSize: '0.82rem', fontWeight: 500, transition: 'all 0.15s',
        }}
        onMouseEnter={e => !loading && (e.currentTarget.style.borderColor = 'var(--primary-light)')}
        onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border)')}
      >
        {loading ? (
          <span style={{ width: 13, height: 13, border: '2px solid #e2e8f0', borderTopColor: 'var(--primary)', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
        ) : (
          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
        )}
        {loading ? 'Exporting\u2026' : label}
        {!loading && <svg width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>}
      </button>
      {open && !loading && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 98 }} onClick={() => setOpen(false)} />
          <div style={{
            position: 'absolute', top: 'calc(100% + 4px)', right: 0,
            background: '#fff', border: '1px solid var(--border)', borderRadius: 8,
            boxShadow: 'var(--shadow-md)', zIndex: 99, overflow: 'hidden', minWidth: 140,
          }}>
            {[
              { fmt: 'xlsx', icon: '📊', label: 'Excel (.xlsx)' },
              { fmt: 'csv',  icon: '📄', label: 'CSV (.csv)' },
            ].map(({ fmt, icon, label: fmtLabel }) => (
              <button key={fmt} onClick={() => doExport(fmt)} style={{
                width: '100%', padding: '9px 14px', textAlign: 'left', background: 'none',
                border: 'none', cursor: 'pointer', fontSize: '0.82rem', color: 'var(--text-primary)',
                display: 'flex', alignItems: 'center', gap: 8,
                borderBottom: fmt === 'xlsx' ? '1px solid var(--border)' : 'none',
              }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}
              >
                <span>{icon}</span>{fmtLabel}
              </button>
            ))}
          </div>
        </>
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}
